interface SEOScoreResult {
  score: number
  maxScore: number
  percentage: number
  issues: string[]
  suggestions: string[]
}

interface SEOData {
  title: string
  description: string
  content: string
  keywords?: string
  slug?: string
  featuredImage?: string
  excerpt?: string
}

export const calculateSEOScore = (data: SEOData): SEOScoreResult => {
  let score = 0
  const maxScore = 100
  const issues: string[] = []
  const suggestions: string[] = []

  // Title checks (30 points)
  if (data.title) {
    score += 10
    if (data.title.length >= 30 && data.title.length <= 60) {
      score += 10
    } else if (data.title.length < 30) {
      issues.push('Title is too short (< 30 characters)')
      suggestions.push('Expand your title to be between 30-60 characters')
    } else {
      issues.push('Title is too long (> 60 characters)')
      suggestions.push('Shorten your title to under 60 characters')
    }

    // Check for keywords in title
    if (data.keywords && data.title.toLowerCase().includes(data.keywords.toLowerCase())) {
      score += 10
    } else if (data.keywords) {
      suggestions.push('Include your focus keyword in the title')
    }
  } else {
    issues.push('No title provided')
  }

  // Description checks (25 points)
  if (data.description) {
    score += 10
    if (data.description.length >= 120 && data.description.length <= 160) {
      score += 10
    } else if (data.description.length < 120) {
      issues.push('Meta description is too short (< 120 characters)')
      suggestions.push('Expand your description to be between 120-160 characters')
    } else {
      issues.push('Meta description is too long (> 160 characters)')
      suggestions.push('Shorten your description to under 160 characters')
    }

    // Check for keywords in description
    if (data.keywords && data.description.toLowerCase().includes(data.keywords.toLowerCase())) {
      score += 5
    } else if (data.keywords) {
      suggestions.push('Include your focus keyword in the meta description')
    }
  } else {
    issues.push('No meta description provided')
  }

  // Content checks (25 points)
  if (data.content) {
    const wordCount = data.content.split(/\s+/).filter(word => word.length > 0).length

    if (wordCount >= 300) {
      score += 15
      if (wordCount >= 600) {
        score += 5
      } else {
        suggestions.push('Consider expanding your content to over 600 words for better SEO')
      }
    } else {
      issues.push('Content is too short (< 300 words)')
      suggestions.push('Add more substantial content (at least 300 words)')
    }

    // Check for keywords in content
    if (data.keywords) {
      const keywordCount = (data.content.toLowerCase().match(new RegExp(data.keywords.toLowerCase(), 'g')) || []).length
      if (keywordCount > 0) {
        score += 5
        if (keywordCount > 3) {
          suggestions.push('Avoid overusing your focus keyword')
        }
      } else {
        suggestions.push('Include your focus keyword naturally in the content')
      }
    }
  } else {
    issues.push('No content provided')
  }

  // URL/Slug checks (10 points)
  if (data.slug) {
    score += 5
    if (data.slug.length <= 50 && !/[^a-z0-9-]/.test(data.slug)) {
      score += 5
    } else {
      if (data.slug.length > 50) {
        issues.push('URL slug is too long')
        suggestions.push('Keep URL slugs concise and under 50 characters')
      }
      if (/[^a-z0-9-]/.test(data.slug)) {
        issues.push('URL slug contains invalid characters')
        suggestions.push('Use only lowercase letters, numbers, and hyphens in URLs')
      }
    }
  } else {
    issues.push('No URL slug provided')
  }

  // Featured image check (5 points)
  if (data.featuredImage) {
    score += 5
  } else {
    suggestions.push('Add a featured image to improve engagement')
  }

  // Excerpt check (5 points)
  if (data.excerpt && data.excerpt.length >= 50) {
    score += 5
  } else {
    suggestions.push('Add a compelling excerpt to improve click-through rates')
  }

  return {
    score,
    maxScore,
    percentage: Math.round((score / maxScore) * 100),
    issues,
    suggestions,
  }
}

export const getSEOScoreColor = (percentage: number): string => {
  if (percentage >= 80) return 'text-green-600'
  if (percentage >= 60) return 'text-yellow-600'
  if (percentage >= 40) return 'text-orange-600'
  return 'text-red-600'
}

export const getSEOScoreLabel = (percentage: number): string => {
  if (percentage >= 80) return 'Excellent'
  if (percentage >= 60) return 'Good'
  if (percentage >= 40) return 'Needs Improvement'
  return 'Poor'
}